import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import SignatureCanvas from "react-signature-canvas";
import * as faceapi from "face-api.js";
import { ref, set, onValue } from "firebase/database";
import { database } from "./firebase";
import {
  getStorage,
  ref as storageRef,
  uploadString,
  getDownloadURL,
} from "firebase/storage";
import BossNavigation from "./BossNavigation";
import Navigation from "./Navigation";

// 🔥 LOAD MODELS
const loadModels = async () => {
  const MODEL_URL = "/models";

  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
  ]);
};

export default function AddEmployee() {
  const webcamRef = useRef(null);
  const signRef = useRef(null);

  const processingRef = useRef(false);

  const [step, setStep] = useState("form");
  const [status, setStatus] = useState("Initializing...");
  const [name, setName] = useState("");

  const [descriptors, setDescriptors] = useState([]);
  const [faceImages, setFaceImages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  const REQUIRED_SAMPLES = 3;

  // 🔥 INIT
  useEffect(() => {
    loadModels().then(() => setStatus("Ready"));

    return onValue(ref(database, "employees"), (snap) => {
      const list = [];
      if (snap.exists()) {
        snap.forEach((c) => list.push({ id: c.key, ...c.val() }));
      }
      setEmployees(list);
    });
  }, []);

  // 📸 CAPTURE FACE
  const handleCapture = async () => {
    if (processingRef.current) return;

    const video = webcamRef.current?.video;

    if (!video || video.readyState !== 4) {
      setStatus("Camera loading...");
      return;
    }

    processingRef.current = true;
    setStatus("Scanning...");

    try {
      const detection = await faceapi
        .detectSingleFace(
          video,
          new faceapi.TinyFaceDetectorOptions({
            inputSize: 224,
            scoreThreshold: 0.5,
          })
        )
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection || !detection.descriptor) {
        setStatus("❌ Face not detected");
        processingRef.current = false;
        return;
      }

      const desc = detection.descriptor;

      // 🔒 DUPLICATE CHECK
      for (let emp of employees) {
        if (!emp.faceDescriptors) continue;

        for (let stored of emp.faceDescriptors) {
          const dist = faceapi.euclideanDistance(
            desc,
            new Float32Array(stored)
          );

          if (dist < 0.5) {
            setStatus(`❌ Already registered as ${emp.name}`);
            processingRef.current = false;
            return;
          }
        }
      }

      // 📸 CAPTURE IMAGE (COMPRESSED)
      const image = webcamRef.current.getScreenshot({
        width: 300,
        height: 200,
      });

      setDescriptors((prev) => [...prev, Array.from(desc)]);
      setFaceImages((prev) => [...prev, image]);

      const count = descriptors.length + 1;

      if (count >= REQUIRED_SAMPLES) {
        setStatus("✅ Face captured!");
        setTimeout(() => setStep("sign"), 500);
      } else {
        setStatus(`Captured ${count}/${REQUIRED_SAMPLES}`);
      }

    } catch (err) {
      console.error(err);
      setStatus("Error detecting face");
    }

    processingRef.current = false;
  };

  // 💾 SAVE EMPLOYEE
  const handleSave = async () => {
    if (saving) return; // 🛑 prevent double click
    setSaving(true);

    const canvas = signRef.current?.getCanvas?.();

    if (!canvas || signRef.current.isEmpty()) {
      setStatus("Draw signature");
      setSaving(false);
      return;
    }

    setStatus("Saving...");

    try {
      const storage = getStorage();

      // 🔥 UNIQUE ID
      const id = `EMP-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      // 🚀 FAST PARALLEL UPLOAD
      const uploadTasks = faceImages.map((img, i) => {
        const fRef = storageRef(storage, `faces/${id}_${i}.jpg`);
        return uploadString(fRef, img, "data_url")
          .then(() => getDownloadURL(fRef));
      });

      const faceUrls = await Promise.all(uploadTasks);

      // 🔥 SIGNATURE
      const sigData = canvas.toDataURL("image/png");
      const sigRef = storageRef(storage, `signatures/${id}.png`);
      await uploadString(sigRef, sigData, "data_url");
      const sigUrl = await getDownloadURL(sigRef);

      // 🔥 SAVE DATABASE
      await set(ref(database, `employees/${id}`), {
        id,
        name,
        faceDescriptors: descriptors,
        faceImageUrls: faceUrls,
        signatureUrl: sigUrl,
        createdAt: new Date().toISOString(),
      });

      setStatus("✅ Employee Added");
      setStep("done");

      // 🔄 AUTO-REDIRECT TO NAME ENTRY AFTER 2 SECONDS
      setTimeout(() => {
        // Reset all state variables
        setName("");
        setDescriptors([]);
        setFaceImages([]);
        setStep("form");
        setStatus("Ready");
        setSaving(false);
        // Clear signature canvas if needed
        if (signRef.current) {
          signRef.current.clear();
        }
      }, 2000);

    } catch (err) {
      console.error(err);
      setStatus("❌ Save failed");
      setSaving(false);
    }
  };

  // 🧹 CLEAR SIGNATURE
  const clearSignature = () => {
    if (signRef.current) {
      signRef.current.clear();
      setStatus("Signature cleared");
    }
  };

  return (
    <div style={s.page}>
       
      <Navigation role="hr" />

      {step === "form" && (
        <div style={s.card}>
          <h2>Add Employee</h2>

          <input
            style={s.input}
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <button style={s.btn} onClick={() => setStep("capture")}>
            Start
          </button>
        </div>
      )}

      {step === "capture" && (
        <div style={s.card}>
          <Webcam
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            style={s.webcam}
          />

          <p>{status}</p>

          <button style={s.btn} onClick={handleCapture}>
            Capture ({descriptors.length}/3)
          </button>
        </div>
      )}

      {step === "sign" && (
        <div style={s.card}>
          <h3>Signature</h3>

          <SignatureCanvas
            ref={signRef}
            canvasProps={{ width: 300, height: 150 }}
          />

          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button 
              style={{ ...s.btn, background: "#ef4444" }} 
              onClick={clearSignature}
            >
              Clear
            </button>
            <button
              style={{ ...s.btn, opacity: saving ? 0.6 : 1 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Employee"}
            </button>
          </div>
        </div>
      )}

      {step === "done" && (
        <div style={s.card}>
          <h2>✅ Employee Added</h2>
          <p>Redirecting to add another employee...</p>
        </div>
      )}
    </div>
  );
}

// 📱 MOBILE UI
const s = {
  page: {
    display: "flex",
    justifyContent: "center",
    padding: 20,
    background: "#0f172a",
    minHeight: "100vh",
    color: "white",
  },
  card: {
    width: "100%",
    maxWidth: 380,
    background: "#1e293b",
    padding: 20,
    borderRadius: 12,
    textAlign: "center",
  },
  input: {
    width: "100%",
    padding: 12,
    marginBottom: 10,
  },
  btn: {
    width: "100%",
    padding: 12,
    background: "#3b82f6",
    border: "none",
    color: "white",
    fontWeight: "bold",
    cursor: "pointer",
  },
  webcam: {
    width: "100%",
    borderRadius: 10,
  },
};